/**
 * Global Error Handler
 *
 * Handles uncaught errors and unhandled promise rejections
 * to prevent application crashes and provide better error reporting.
 */

/**
 * Log error to console and optionally to external service
 */
const logError = (error: Error | string, context?: string) => {
  const errorMessage = error instanceof Error ? error.message : error;
  const errorStack = error instanceof Error ? error.stack : undefined;

  console.error(`[Global Error Handler] ${context || 'Uncaught Error'}:`, {
    message: errorMessage,
    stack: errorStack,
    timestamp: new Date().toISOString(),
  });

  // In production, send to error monitoring service
  if (process.env.NODE_ENV === 'production') {
    // TODO: Send to error monitoring service (Sentry, LogRocket, etc.)
    // Example: Sentry.captureException(error);
  }
};

/**
 * Handle uncaught errors
 */
const handleError = (event: ErrorEvent) => {
  event.preventDefault(); // Prevent default browser error handling

  logError(event.error || event.message, 'Uncaught Error');

  // Optionally show user-friendly error notification
  // You could dispatch a Redux action or show a toast notification here
};

/**
 * Handle unhandled promise rejections
 */
const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  event.preventDefault(); // Prevent default browser error handling

  const reason = event.reason;
  const errorMessage = reason instanceof Error ? reason : String(reason);

  logError(errorMessage, 'Unhandled Promise Rejection');

  // Optionally show user-friendly error notification
  // You could dispatch a Redux action or show a toast notification here
};

/**
 * Setup global error handlers
 * Should be called once at application startup
 */
export const setupGlobalErrorHandlers = () => {
  // Handle uncaught errors
  window.addEventListener('error', handleError);

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // Log successful setup
  console.log('[Global Error Handler] Error handlers initialized');
};

/**
 * Remove global error handlers
 * Should be called when cleaning up (e.g., in tests)
 */
export const removeGlobalErrorHandlers = () => {
  window.removeEventListener('error', handleError);
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
};

/**
 * Manually report an error to the error monitoring service
 */
export const reportError = (error: Error | string, context?: Record<string, any>) => {
  logError(error, context ? JSON.stringify(context) : undefined);
};
